import type { LocationFilter } from "@/components/WebLocationSearch.vue";
import { framingBbox } from "@carnet/ui/utils/geo";
import { apiRadiusKm } from "@carnet/ui/utils/location";

const MAX_LABEL_LENGTH = 200;
const MAX_RADIUS_KM = 5000;

function readNumber(raw: unknown): number | undefined {
  if (typeof raw !== "string" || !raw.length) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function readString(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  if (!trimmed.length || trimmed.length > MAX_LABEL_LENGTH) return undefined;
  return trimmed;
}

function readBbox(raw: unknown): [number, number, number, number] | undefined {
  if (typeof raw !== "string") return undefined;
  const parts = raw.split(",").map(Number);
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return undefined;
  return parts as [number, number, number, number];
}

function writeBbox(b: [number, number, number, number]): string {
  return b.map((n) => Number(n.toFixed(5))).join(",");
}

export function useUrlLocationFilter(opts: { withRadius?: boolean } = {}) {
  const route = useRoute();
  const router = useRouter();

  function setFilter(f: LocationFilter) {
    const query = { ...route.query };
    if (f.location && f.center) {
      query.q = f.location.name;
      query.lat = String(f.center[1]);
      query.lng = String(f.center[0]);
      if (opts.withRadius && f.radius) query.radius = String(f.radius);
      else delete query.radius;
      if (f.location.bbox) query.bbox = writeBbox(f.location.bbox);
      else delete query.bbox;
    } else {
      delete query.q;
      delete query.lat;
      delete query.lng;
      delete query.radius;
      delete query.bbox;
    }
    return router.push({ query });
  }

  const filter = computed<LocationFilter>({
    get: () => {
      const name = readString(route.query.q);
      const lat = readNumber(route.query.lat);
      const lng = readNumber(route.query.lng);
      if (!name || lat === undefined || lng === undefined) return {};

      const center: [number, number] = [lng, lat];
      const r = opts.withRadius ? readNumber(route.query.radius) : undefined;
      const radius = r !== undefined && r > 0 && r <= MAX_RADIUS_KM ? r : undefined;
      const mapboxBbox = readBbox(route.query.bbox);

      const base = { location: { center, name, placeFormatted: "", bbox: mapboxBbox }, center, radius };
      return { ...base, bbox: framingBbox(mapboxBbox, center, apiRadiusKm(base)) };
    },
    set: setFilter,
  });

  function clear() {
    return setFilter({});
  }

  const chip = computed<{ label: string } | undefined>(() => {
    const loc = filter.value.location;
    if (!loc) return undefined;
    const r = filter.value.radius;
    return { label: r ? `${loc.name} (${r}\u202fkm)` : loc.name };
  });

  return { filter, setFilter, clear, chip };
}
